import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Loader, Stack, Text } from '@mantine/core';
import { IconPlus, IconSitemap } from '@tabler/icons-react';
import { MaintenanceTabPanelHeader } from '../../components/common/MaintenanceTabPanelHeader';
import {
    GET_SAP_DIVISIONS_FOR_VIRTUAL_BUILDER,
    GET_VIRTUAL_DIVISIONS,
    GET_VIRTUAL_HIERARCHY_ELIGIBLE_USERS,
    GET_VIRTUAL_OPS_AREAS,
} from '../../apollo/queries/virtualHierarchy';
import {
    CREATE_VIRTUAL_DIVISION,
    DELETE_VIRTUAL_DIVISION,
    UPDATE_VIRTUAL_DIVISION,
} from '../../apollo/mutations/virtualHierarchy';
import { ConfirmModal } from '../../components/common';
import { VirtualHierarchyBuilderModal } from '../../components/virtual-hierarchy/VirtualHierarchyBuilderModal';
import { getOccupiedStoreIds } from '../../components/virtual-hierarchy/helpers';
import {
    VirtualHierarchyEmptyState,
    VirtualHierarchyOverlayCard,
} from '../../components/virtual-hierarchy/VirtualHierarchyOverlayCard';
import {
    SapDivisionOption,
    VirtualDivisionInputPayload,
    VirtualDivisionRow,
    VirtualHierarchyUser,
    VirtualOpsAreaRow,
} from '../../components/virtual-hierarchy/types';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';

type VirtualDivisionsQueryData = {
    getVirtualDivisions: VirtualDivisionRow[];
};

type VirtualOpsAreasQueryData = {
    getVirtualOpsAreas: VirtualOpsAreaRow[];
};

type SapDivisionsQueryData = {
    getSapDivisionsForVirtualBuilder: SapDivisionOption[];
};

type EligibleUsersQueryData = {
    getVirtualHierarchyEligibleUsers: VirtualHierarchyUser[];
};

function VirtualHierarchyManager() {
    const [builderOpened, setBuilderOpened] = useState(false);
    const [editingDivision, setEditingDivision] = useState<
        VirtualDivisionRow | undefined
    >();
    const [deletingDivision, setDeletingDivision] = useState<
        VirtualDivisionRow | undefined
    >();

    const {
        data,
        loading: divisionsLoading,
        error: divisionsError,
    } = useQuery<VirtualDivisionsQueryData>(GET_VIRTUAL_DIVISIONS);
    const {
        data: opsAreaData,
        loading: opsAreasLoading,
        error: opsAreasError,
    } = useQuery<VirtualOpsAreasQueryData>(GET_VIRTUAL_OPS_AREAS);
    const {
        data: sapDivisionData,
        loading: sapDivisionsLoading,
        error: sapDivisionsError,
    } = useQuery<SapDivisionsQueryData>(GET_SAP_DIVISIONS_FOR_VIRTUAL_BUILDER);
    const {
        data: usersData,
        loading: usersLoading,
        error: usersError,
    } = useQuery<EligibleUsersQueryData>(GET_VIRTUAL_HIERARCHY_ELIGIBLE_USERS);
    const loading =
        divisionsLoading ||
        opsAreasLoading ||
        sapDivisionsLoading ||
        usersLoading;
    const error =
        divisionsError ?? opsAreasError ?? sapDivisionsError ?? usersError;

    const divisions = data?.getVirtualDivisions ?? [];
    const virtualOpsAreas = opsAreaData?.getVirtualOpsAreas ?? [];
    const sapDivisions =
        sapDivisionData?.getSapDivisionsForVirtualBuilder ?? [];
    const users = usersData?.getVirtualHierarchyEligibleUsers ?? [];
    const occupiedStoreIds = useMemo(
        () => getOccupiedStoreIds(divisions, editingDivision?.id),
        [divisions, editingDivision?.id],
    );

    const refetchQueries = [
        { query: GET_VIRTUAL_DIVISIONS },
        { query: GET_VIRTUAL_OPS_AREAS },
    ];

    const [createVirtualDivision, { loading: creating }] = useMutation(
        CREATE_VIRTUAL_DIVISION,
        { refetchQueries, awaitRefetchQueries: true },
    );
    const [updateVirtualDivision, { loading: updating }] = useMutation(
        UPDATE_VIRTUAL_DIVISION,
        { refetchQueries, awaitRefetchQueries: true },
    );
    const [deleteVirtualDivision, { loading: deleting }] = useMutation(
        DELETE_VIRTUAL_DIVISION,
        { refetchQueries, awaitRefetchQueries: true },
    );

    const handleCreate = () => {
        setEditingDivision(undefined);
        setBuilderOpened(true);
    };

    const handleEdit = (division: VirtualDivisionRow) => {
        setEditingDivision(division);
        setBuilderOpened(true);
    };

    const handleCloseBuilder = () => {
        setBuilderOpened(false);
        setEditingDivision(undefined);
    };

    const handleSubmit = async (input: VirtualDivisionInputPayload) => {
        try {
            if (editingDivision) {
                await updateVirtualDivision({
                    variables: { id: editingDivision.id, input },
                });
                showSuccessNotification('Virtual division updated');
                return;
            }
            await createVirtualDivision({ variables: { input } });
            showSuccessNotification('Virtual division created');
        } catch (submitError) {
            const message =
                submitError instanceof Error
                    ? submitError.message
                    : 'Failed to save virtual division';
            showErrorNotification(message);
            throw submitError;
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deletingDivision) {
            return;
        }
        try {
            await deleteVirtualDivision({
                variables: { id: deletingDivision.id },
            });
            showSuccessNotification(
                `Deleted virtual division ${deletingDivision.name}`,
            );
        } catch (deleteError) {
            const message =
                deleteError instanceof Error
                    ? deleteError.message
                    : 'Failed to delete virtual division';
            showErrorNotification(message);
            throw deleteError;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center p-6">
                <Loader size="sm" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert color="red" title="Error loading virtual hierarchy">
                {error.message}
            </Alert>
        );
    }

    const storeCount = deletingDivision?.stores.length ?? 0;

    return (
        <div>
            <MaintenanceTabPanelHeader>
                <div className="flex items-center w-full">
                    <div className="flex items-center gap-2">
                        <IconSitemap size={24} />
                        <span className="text-lg font-medium">
                            Virtual Hierarchy
                        </span>
                    </div>
                    <div className="ml-auto flex items-center">
                        <Button
                            type="button"
                            size="sm"
                            leftSection={<IconPlus size={16} />}
                            onClick={handleCreate}
                        >
                            Create
                        </Button>
                    </div>
                </div>
                <Text size="sm" c="dimmed" mt="xs">
                    Virtual divisions sit on SAP without moving stores. A
                    virtual ops area is optional.
                </Text>
            </MaintenanceTabPanelHeader>

            {divisions.length === 0 ? (
                <VirtualHierarchyEmptyState />
            ) : (
                <Stack gap="md">
                    {divisions.map(division => (
                        <VirtualHierarchyOverlayCard
                            key={division.id}
                            division={division}
                            deleting={
                                deleting && deletingDivision?.id === division.id
                            }
                            onEdit={handleEdit}
                            onDelete={setDeletingDivision}
                        />
                    ))}
                </Stack>
            )}

            {builderOpened ? (
                <VirtualHierarchyBuilderModal
                    key={editingDivision?.id ?? 'create'}
                    opened
                    onClose={handleCloseBuilder}
                    editingDivision={editingDivision}
                    users={users}
                    sapDivisions={sapDivisions}
                    virtualOpsAreas={virtualOpsAreas}
                    occupiedStoreIds={occupiedStoreIds}
                    saving={creating || updating}
                    onSubmit={handleSubmit}
                />
            ) : null}

            <ConfirmModal
                opened={Boolean(deletingDivision)}
                onClose={() => setDeletingDivision(undefined)}
                title="Delete virtual division?"
                confirmLabel="Delete"
                confirmColor="red"
                onConfirm={handleDeleteConfirm}
                loading={deleting}
            >
                <Text size="sm" c="dark.7">
                    {deletingDivision
                        ? `This will detach ${storeCount} ${
                              storeCount === 1 ? 'store' : 'stores'
                          } from ${
                              deletingDivision.name
                          }. Stores stay on their SAP divisions. Open work stays with the current virtual divisional manager. This cannot be undone.`
                        : 'This action cannot be undone.'}
                </Text>
            </ConfirmModal>
        </div>
    );
}

export default VirtualHierarchyManager;
